import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  ActivateGroupDocument,
  type ActivateGroupMutation,
  type ActivateGroupMutationVariables,
  DeactivateGroupDocument,
  type DeactivateGroupMutation,
  type DeactivateGroupMutationVariables,
  GetGroupsDocument,
} from "~/features/admin/groups/gqls";
import { translateError } from "~/lib/errors";

interface GroupActivationTarget {
  id: string;
}

/**
 * Activate / deactivate a group. Returns plain submitters and a `processing`
 * flag; the calling view owns the confirmation dialog (V1 parity).
 */
export function useGroupActivation() {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);

  const [activateGroup] = useMutation<ActivateGroupMutation, ActivateGroupMutationVariables>(ActivateGroupDocument, {
    refetchQueries: [GetGroupsDocument],
  });
  const [deactivateGroup] = useMutation<DeactivateGroupMutation, DeactivateGroupMutationVariables>(
    DeactivateGroupDocument,
    { refetchQueries: [GetGroupsDocument] },
  );

  const onActivate = useCallback(
    async ({ id }: GroupActivationTarget) => {
      setProcessing(true);
      try {
        await activateGroup({ variables: { id } });
        toast.success(t("feature.common.activatedSuccessfully", { model: "group" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [activateGroup, t],
  );

  const onDeactivate = useCallback(
    async ({ id }: GroupActivationTarget) => {
      setProcessing(true);
      try {
        await deactivateGroup({ variables: { id } });
        toast.success(t("feature.common.deactivatedSuccessfully", { model: "group" }));
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [deactivateGroup, t],
  );

  return { onActivate, onDeactivate, processing };
}
